/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { FadeUpStagger, MotionCard, MotionButton } from "../motion/AdminMotion";
import { adminGlass } from "../../styles/adminGlassTokens";

function formatCount(value) {
  return Number(value || 0).toLocaleString();
}

function displayClassName(item) {
  return item?.name || item?.className || "Unnamed Class";
}

function parseCsvLine(line) {
  const result = [];
  let current = "";
  let insideQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"' && insideQuotes && nextChar === '"') {
      current += '"';
      i += 1;
    } else if (char === '"') {
      insideQuotes = !insideQuotes;
    } else if (char === "," && !insideQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }

  result.push(current.trim());

  return result.map((v) =>
    String(v || "")
      .replace(/^"|"$/g, "")
      .trim()
  );
}

function normalizeCsvHeader(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
}

function cardTone(tone) {
  const tones = {
    blue: {
      value: "#2845d9",
      bg: "linear-gradient(180deg, rgba(255,255,255,.86), rgba(239,246,255,.72))",
      border: "rgba(191,219,254,.95)",
    },
    green: {
      value: "#078348",
      bg: "linear-gradient(180deg, rgba(255,255,255,.86), rgba(236,253,245,.74))",
      border: "rgba(187,247,208,.95)",
    },
    amber: {
      value: "#c44a0a",
      bg: "linear-gradient(180deg, rgba(255,255,255,.86), rgba(255,251,235,.74))",
      border: "rgba(253,230,138,.95)",
    },
    red: {
      value: "#c42424",
      bg: "linear-gradient(180deg, rgba(255,255,255,.86), rgba(254,242,242,.74))",
      border: "rgba(254,202,202,.95)",
    },
  };

  return tones[tone] || tones.blue;
}

export default function ClassesSection({ schoolId }) {
  const currentSchoolId = schoolId || "";

  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [className, setClassName] = useState("");
  const [yearGroup, setYearGroup] = useState("");
  const [search, setSearch] = useState("");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busyId, setBusyId] = useState("");
  const [csvRows, setCsvRows] = useState([]);
const [csvBusy, setCsvBusy] = useState(false);
const [csvError, setCsvError] = useState("");
const [csvFileName, setCsvFileName] = useState("");

  const [editingClass, setEditingClass] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    yearGroup: "",
  });

  async function loadClasses() {
    if (!currentSchoolId) {
      setClasses([]);
      setStudents([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const [classesSnap, studentsSnap] = await Promise.all([
        getDocs(collection(db, "schools", currentSchoolId, "classes")),
        getDocs(collection(db, "schools", currentSchoolId, "students")),
      ]);

      const classList = classesSnap.docs
        .map((d) => ({
          id: d.id,
          ...d.data(),
        }))
        .filter((item) => !item.deletedAt)
        .sort((a, b) => displayClassName(a).localeCompare(displayClassName(b)));

      const studentList = studentsSnap.docs
        .map((d) => ({
          id: d.id,
          ...d.data(),
        }))
        .filter((item) => !item.deletedAt);

      setClasses(classList);
      setStudents(studentList);
    } catch (err) {
      console.error(err);
      setError("Failed to load classes.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadClasses();
  }, [currentSchoolId]);

  const classStudentCountMap = useMemo(() => {
    const map = {};
    classes.forEach((item) => {
      map[item.id] = 0;
    });

    students.forEach((student) => {
      if (student.classId && map[student.classId] !== undefined) {
        map[student.classId] += 1;
      }
    });

    return map;
  }, [classes, students]);

  const summary = useMemo(() => {
    const withStudents = classes.filter(
      (c) => (classStudentCountMap[c.id] || 0) > 0
    ).length;

    return {
      total: classes.length,
      withStudents,
      empty: Math.max(0, classes.length - withStudents),
      students: students.length,
    };
  }, [classes, students, classStudentCountMap]);

  const filteredClasses = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return classes;

    return classes.filter((item) => {
      const hay = [item.name, item.className, item.yearGroup, item.id]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }, [classes, search]);

async function handleCsvSelected(e) {
  const file = e.target.files?.[0];
  if (!file) return;

  try {
    setCsvBusy(true);
    setCsvError("");
    setCsvRows([]);
    setCsvFileName(file.name || "");

    const text = await file.text();

    const lines = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    if (lines.length < 2) {
      throw new Error(
        "CSV must include a header row and at least one class."
      );
    }

    const headers = parseCsvLine(lines[0]).map(normalizeCsvHeader);

    const requiredHeaders = ["classname", "yeargroup"];

    const validHeaders =
      headers.length === requiredHeaders.length &&
      requiredHeaders.every((h) => headers.includes(h));

    if (!validHeaders) {
      throw new Error(
        "Invalid CSV format. Required headers: Class Name, Year Group"
      );
    }

    const existingNames = new Set(
      classes.map((c) =>
        String(c.name || c.className || "")
          .trim()
          .toLowerCase()
      )
    );

    const rows = lines.slice(1).map((line, index) => {
      const cells = parseCsvLine(line);

      const row = {};

      headers.forEach((header, i) => {
        row[header] = cells[i] || "";
      });

      const className = String(row.classname || "").trim();
      const yearGroup = String(row.yeargroup || "").trim();

      const errors = [];

      if (!className) {
        errors.push("Missing class name");
      }

      if (!yearGroup) {
        errors.push("Missing year group");
      }

      if (
        className &&
        existingNames.has(className.toLowerCase())
      ) {
        errors.push("Class already exists");
      }

      return {
        rowNumber: index + 2,
        className,
        yearGroup,
        errors,
      };
    });

    const hasErrors = rows.some((r) => r.errors.length);

    if (hasErrors) {
      throw new Error(
        "CSV contains invalid rows. Fix errors before importing."
      );
    }

    setCsvRows(rows);
  } catch (err) {
    console.error(err);
    setCsvError(err?.message || "Failed to read CSV.");
  } finally {
    setCsvBusy(false);
    e.target.value = "";
  }
}

async function handleImportClassesCsv() {
  if (!csvRows.length) return;

  try {
    setCsvBusy(true);
    setCsvError("");
    setError("");
    setSuccess("");

    for (const row of csvRows) {
      await addDoc(
        collection(db, "schools", currentSchoolId, "classes"),
        {
          schoolId: currentSchoolId,
          name: row.className,
          className: row.className,
          yearGroup: row.yearGroup,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }
      );
    }

    setSuccess(
      `${csvRows.length} classes imported successfully.`
    );

    setCsvRows([]);
    setCsvFileName("");

    await loadClasses();
  } catch (err) {
    console.error(err);
    setCsvError(err?.message || "Failed to import classes.");
  } finally {
    setCsvBusy(false);
  }
}

  async function handleCreate(e) {
    e.preventDefault();

    const cleanName = String(className || "").trim();
    const cleanYearGroup = String(yearGroup || "").trim();

    if (!cleanName || !cleanYearGroup) {
      setError("Please fill all fields.");
      return;
    }

    try {
      setError("");
      setSuccess("");

      await addDoc(collection(db, "schools", currentSchoolId, "classes"), {
        schoolId: currentSchoolId,
        name: cleanName,
        className: cleanName,
        yearGroup: cleanYearGroup,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await setDoc(
        doc(db, "schools", currentSchoolId, "settings", "school"),
        {
          classesReady: true,
          onboardingStep: 3,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setClassName("");
      setYearGroup("");

      await loadClasses();
      setSuccess("Class created successfully.");
    } catch (err) {
      console.error("Create class error:", err);
      setError(err?.message || "Failed to create class.");
    }
  }

  function openEditModal(item) {
    setEditingClass(item);
    setEditForm({
      name: item.name || item.className || "",
      yearGroup: item.yearGroup || "",
    });
  }

  function closeEditModal() {
    setEditingClass(null);
    setEditForm({
      name: "",
      yearGroup: "",
    });
  }

  async function handleSaveEdit(e) {
    e.preventDefault();
    if (!editingClass?.id) return;

    const cleanName = String(editForm.name || "").trim();
    const cleanYearGroup = String(editForm.yearGroup || "").trim();

    if (!cleanName || !cleanYearGroup) {
      setError("Please fill all fields.");
      return;
    }

    try {
      setBusyId(editingClass.id);
      setError("");
      setSuccess("");

      await setDoc(
        doc(db, "schools", currentSchoolId, "classes", editingClass.id),
        {
          schoolId: currentSchoolId,
          name: cleanName,
          className: cleanName,
          yearGroup: cleanYearGroup,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await loadClasses();
      closeEditModal();
      setSuccess("Class updated successfully.");
    } catch (err) {
      console.error("Update class error:", err);
      setError(err?.message || "Failed to update class.");
    } finally {
      setBusyId("");
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this class?")) return;

    try {
      setBusyId(id);
      setError("");
      setSuccess("");

      await deleteDoc(doc(db, "schools", currentSchoolId, "classes", id));
      await loadClasses();
      setSuccess("Class deleted successfully.");
    } catch (err) {
      console.error(err);
      setError("Failed to delete class.");
    } finally {
      setBusyId("");
    }
  }

  return (
    <div style={styles.page}>
      <MotionCard style={styles.hero} hover>
        <div>
          <div style={styles.heroEyebrow}>PresentPlus Admin</div>
          <h2 style={styles.heroTitle}>Classes</h2>
          <p style={styles.heroText}>
            Create class groups first, then assign students and teachers into a
            clean school structure.
          </p>
        </div>

        <div style={styles.heroBadge}>
          <div style={styles.heroBadgeLabel}>Total classes</div>
          <div style={styles.heroBadgeValue}>{formatCount(summary.total)}</div>
        </div>
      </MotionCard>

      {error ? <div style={styles.errorBox}>{error}</div> : null}
      {success ? <div style={styles.successBox}>{success}</div> : null}

      <section style={styles.statGrid}>
        <FadeUpStagger>
          <StatCard label="Total classes" value={summary.total} tone="blue" />
          <StatCard label="With students" value={summary.withStudents} tone="green" />
          <StatCard label="Empty classes" value={summary.empty} tone="amber" />
          <StatCard label="Students linked" value={summary.students} tone="blue" />
        </FadeUpStagger>
      </section>

      <MotionCard style={styles.panel} hover>
        <div style={styles.panelHead}>
          <div>
            <div style={styles.panelEyebrow}>Class setup</div>
            <h3 style={styles.panelTitle}>Create class</h3>
            <div style={styles.panelText}>
              Start with simple class groups such as Nursery, Reception, Year 1,
              or Year 2.
            </div>
          </div>
        </div>

        <form onSubmit={handleCreate} style={styles.formRow}>
          <input
            style={styles.input}
            placeholder="Class name e.g. Nursery"
            value={className}
            onChange={(e) => setClassName(e.target.value)}
          />

          <input
            style={styles.input}
            placeholder="Year group e.g. Nursery / Year 1"
            value={yearGroup}
            onChange={(e) => setYearGroup(e.target.value)}
          />

          <MotionButton type="submit" style={styles.primaryBtn}>
            Save Class
          </MotionButton>
        </form>

        <div style={{ marginTop: 20 }}>
  <div style={styles.panelEyebrow}>Bulk import</div>

  <div style={styles.csvInfoBox}>
    <div>
      <div style={styles.csvTitle}>
        {csvFileName || "No CSV selected"}
      </div>

      <div style={styles.csvText}>
        Required headers: Class Name, Year Group
      </div>
    </div>

    <label style={styles.primaryBtn}>
      Choose CSV
      <input
        type="file"
        accept=".csv,text/csv"
        onChange={handleCsvSelected}
        style={{ display: "none" }}
      />
    </label>
  </div>

  {csvError ? (
    <div style={styles.errorBox}>{csvError}</div>
  ) : null}

  {csvRows.length ? (
    <div style={styles.csvPreview}>
      {csvRows.map((row) => (
        <div
          key={`${row.rowNumber}-${row.className}`}
          style={styles.csvPreviewRow}
        >
          <strong>{row.className}</strong>
          <span>{row.yearGroup}</span>
        </div>
      ))}
    </div>
  ) : null}

  <div style={{ marginTop: 14 }}>
    <MotionButton
      onClick={handleImportClassesCsv}
      disabled={!csvRows.length || csvBusy}
      style={{
        ...styles.primaryBtn,
        ...((!csvRows.length || csvBusy)
          ? styles.disabledBtn
          : {}),
      }}
    >
      {csvBusy
        ? "Working..."
        : `Import ${csvRows.length || 0} classes`}
    </MotionButton>
  </div>
</div>
      </MotionCard>

      <MotionCard style={styles.panel} hover>
        <div style={styles.panelHeadRow}>
          <div>
            <div style={styles.panelEyebrow}>Class directory</div>
            <h3 style={styles.panelTitle}>Operational list</h3>
            <div style={styles.panelText}>
              Search existing classes, check student counts, and manage updates.
            </div>
          </div>
        </div>

        <div style={styles.controlsRow}>
          <input
            style={styles.searchInput}
            placeholder="Search by class name, year group, or ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <div style={styles.emptyState}>Loading classes...</div>
        ) : filteredClasses.length ? (
          <div style={styles.classList}>
            <FadeUpStagger delay={0.04} gap={0.04}>
              {filteredClasses.map((item) => (
                <div key={item.id} style={styles.classCard}>
                  <div style={styles.classMain}>
                    <div style={styles.classIcon}>
                      {displayClassName(item).charAt(0).toUpperCase()}
                    </div>

                    <div>
                      <div style={styles.className}>{displayClassName(item)}</div>
                      <div style={styles.classSub}>ID: {item.id}</div>
                    </div>
                  </div>

                  <div style={styles.classMeta}>
                    <div style={styles.metaLabel}>Year group</div>
                    <div style={styles.metaValue}>{item.yearGroup || "—"}</div>
                  </div>

                  <div style={styles.classMeta}>
                    <div style={styles.metaLabel}>Students</div>
                    <div style={styles.metaValue}>
                      {formatCount(classStudentCountMap[item.id] || 0)}
                    </div>
                  </div>

                  <div style={styles.actionWrap}>
                    <MotionButton
                      onClick={() => openEditModal(item)}
                      style={styles.editBtn}
                      disabled={busyId === item.id}
                    >
                      Edit
                    </MotionButton>

                    <MotionButton
                      onClick={() => handleDelete(item.id)}
                      style={styles.deleteBtn}
                      disabled={busyId === item.id}
                    >
                      {busyId === item.id ? "Working..." : "Delete"}
                    </MotionButton>
                  </div>
                </div>
              ))}
            </FadeUpStagger>
          </div>
        ) : (
          <div style={styles.emptyState}>
            No classes yet. Create your first class to unlock teacher assignment.
          </div>
        )}
      </MotionCard>

      {editingClass ? (
        <div style={styles.modalOverlay} onClick={closeEditModal}>
          <MotionCard style={styles.modalCard} hover={false}>
            <div onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <div>
                  <div style={styles.panelEyebrow}>Class management</div>
                  <h3 style={styles.modalTitle}>Edit class</h3>
                  <div style={styles.panelText}>
                    Update the class name and year group.
                  </div>
                </div>

                <MotionButton onClick={closeEditModal} style={styles.modalCloseBtn}>
                  <span style={{ transform: "translateY(-1px)" }}>×</span>
                </MotionButton>
              </div>

              <form onSubmit={handleSaveEdit} style={styles.formGrid}>
                <input
                  style={styles.input}
                  placeholder="Class name"
                  value={editForm.name}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, name: e.target.value }))
                  }
                />

                <input
                  style={styles.input}
                  placeholder="Year group"
                  value={editForm.yearGroup}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, yearGroup: e.target.value }))
                  }
                />

                <div style={styles.modalActions}>
                  <MotionButton
                    type="button"
                    onClick={closeEditModal}
                    style={styles.secondaryBtn}
                    disabled={busyId === editingClass.id}
                  >
                    Cancel
                  </MotionButton>

                  <MotionButton
                    type="submit"
                    style={styles.primaryBtn}
                    disabled={busyId === editingClass.id}
                  >
                    {busyId === editingClass.id ? "Saving..." : "Save Changes"}
                  </MotionButton>
                </div>
              </form>
            </div>
          </MotionCard>
        </div>
      ) : null}
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const t = cardTone(tone);

  return (
    <MotionCard
      style={{
        ...styles.statCard,
        background: t.bg,
        border: `1px solid ${t.border}`,
      }}
      hover
    >
      <div style={styles.statLabel}>{label}</div>
      <div style={{ ...styles.statValue, color: t.value }}>
        {formatCount(value)}
      </div>
    </MotionCard>
  );
}

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    gap: 22,
    position: "relative",
    isolation: "isolate",
  },

  hero: {
    borderRadius: 28,
    padding: "30px 28px",
    background: "var(--pp-brand-gradient)",
    color: "#fff",
    boxShadow: "0 28px 70px rgba(79, 99, 255, 0.18)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 20,
  },

  heroEyebrow: {
    fontSize: 12,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    fontWeight: 800,
    opacity: 0.86,
    marginBottom: 10,
  },

  heroTitle: {
    margin: 0,
    fontSize: 42,
    lineHeight: 1.04,
    letterSpacing: "-0.03em",
    fontWeight: 900,
  },

  heroText: {
    margin: "12px 0 0",
    fontSize: 16,
    lineHeight: 1.65,
    opacity: 0.95,
    maxWidth: 760,
  },

  heroBadge: {
    minWidth: 220,
    borderRadius: 24,
    padding: "18px 20px",
    background: "rgba(255,255,255,0.14)",
    border: "1px solid rgba(255,255,255,0.18)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    boxShadow: "0 16px 36px rgba(18, 28, 80, 0.16)",
  },

  heroBadgeLabel: {
    fontSize: 12,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.82,
    marginBottom: 8,
    fontWeight: 700,
  },

  heroBadgeValue: {
    fontSize: 34,
    fontWeight: 900,
    lineHeight: 1,
    letterSpacing: "-0.03em",
  },

  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 18,
    alignItems: "stretch",
  },

  statCard: {
    minHeight: 132,
    borderRadius: 28,
    padding: "22px 22px",
    boxShadow:
      "0 26px 64px rgba(17,32,66,0.09), inset 0 1px 0 rgba(255,255,255,0.78)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },

  statLabel: {
    fontSize: 12,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    fontWeight: 900,
    color: "#5d6b8a",
    marginBottom: 12,
  },

  statValue: {
    fontSize: 46,
    fontWeight: 950,
    lineHeight: 1,
    letterSpacing: "-0.05em",
  },

  panel: {
    ...adminGlass.panel,
    padding: 28,
  },

  panelHead: {
    marginBottom: 18,
  },

  panelHeadRow: {
    marginBottom: 18,
  },

  panelEyebrow: {
    fontSize: 11,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#6b7aa0",
    fontWeight: 900,
    marginBottom: 8,
  },

  panelTitle: {
    margin: 0,
    fontSize: 30,
    lineHeight: 1.05,
    letterSpacing: "-0.04em",
    fontWeight: 950,
    color: "#112042",
  },

  panelText: {
    marginTop: 8,
    color: "#667085",
    fontSize: 15,
    lineHeight: 1.6,
  },

  formRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) auto",
    gap: 12,
    marginTop: 10,
    alignItems: "center",
  },

  formGrid: {
    display: "grid",
    gap: 14,
  },

  input: {
    padding: "15px 16px",
    borderRadius: 18,
    border: "1px solid rgba(219,231,255,0.95)",
    background: "rgba(255,255,255,0.72)",
    fontSize: 15,
    color: "#14213d",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    fontWeight: 700,
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
  },

  primaryBtn: {
    padding: "15px 20px",
    background: "var(--pp-brand-gradient)",
    color: "#fff",
    borderRadius: 18,
    border: "none",
    fontWeight: 950,
    cursor: "pointer",
    fontSize: 15,
    boxShadow: "0 18px 36px rgba(79, 99, 255, 0.18)",
  },

  secondaryBtn: {
    padding: "15px 20px",
    borderRadius: 18,
    border: "1px solid rgba(219,231,255,0.95)",
    background: "rgba(255,255,255,0.72)",
    color: "#334155",
    fontWeight: 950,
    cursor: "pointer",
    fontSize: 15,
  },

  controlsRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: 14,
    alignItems: "center",
    marginBottom: 18,
  },

  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "15px 16px",
    borderRadius: 18,
    border: "1px solid rgba(219,231,255,0.95)",
    background: "rgba(255,255,255,0.72)",
    fontSize: 15,
    color: "#14213d",
    outline: "none",
    fontWeight: 700,
  },

  classList: {
    display: "grid",
    gap: 12,
  },

  classCard: {
    display: "grid",
    gridTemplateColumns: "minmax(260px, 1.4fr) minmax(160px, .7fr) minmax(120px, .5fr) auto",
    gap: 14,
    alignItems: "center",
    padding: 16,
    borderRadius: 24,
    background: "rgba(255,255,255,0.58)",
    border: "1px solid rgba(219,231,255,0.86)",
    boxShadow: "0 10px 24px rgba(17,32,66,0.04)",
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
  },

  classMain: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  },

  classIcon: {
    width: 46,
    height: 46,
    borderRadius: 16,
    display: "grid",
    placeItems: "center",
    background: "var(--pp-brand-gradient)",
    color: "#fff",
    fontWeight: 950,
    flexShrink: 0,
  },

  className: {
    fontSize: 16,
    fontWeight: 950,
    color: "#15284b",
    marginBottom: 4,
  },

  classSub: {
    fontSize: 13,
    color: "#6b7a99",
    wordBreak: "break-word",
  },

  classMeta: {
    minWidth: 0,
  },

  metaLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "#64748b",
    fontWeight: 900,
    marginBottom: 7,
  },

  metaValue: {
    color: "#112042",
    fontWeight: 900,
    fontSize: 15,
  },

  actionWrap: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    flexWrap: "wrap",
  },

  editBtn: {
    padding: "10px 14px",
    borderRadius: 14,
    border: "1px solid #cfe0ff",
    background: "#eef4ff",
    color: "#1d4ed8",
    cursor: "pointer",
    fontWeight: 900,
  },

  deleteBtn: {
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    padding: "10px 14px",
    borderRadius: 14,
    color: "#be123c",
    cursor: "pointer",
    fontWeight: 900,
  },

  disabledBtn: {
  opacity: 0.55,
  cursor: "not-allowed",
},

csvInfoBox: {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 16,
  padding: 18,
  borderRadius: 22,
  background: "rgba(255,255,255,0.58)",
  border: "1px solid rgba(219,231,255,0.86)",
  marginTop: 12,
},

csvTitle: {
  color: "#112042",
  fontWeight: 950,
  marginBottom: 4,
},

csvText: {
  color: "#64748b",
  fontSize: 14,
  lineHeight: 1.5,
},

csvPreview: {
  display: "grid",
  gap: 10,
  marginTop: 14,
},

csvPreviewRow: {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12,
  padding: 14,
  borderRadius: 18,
  background: "rgba(255,255,255,0.58)",
  border: "1px solid rgba(219,231,255,0.86)",
  color: "#334155",
  fontSize: 14,
},

  emptyState: {
    padding: 20,
    borderRadius: 18,
    background: "rgba(255,255,255,0.58)",
    border: "1px solid rgba(219,231,255,0.86)",
    color: "#6b7a99",
    fontSize: 15,
    fontWeight: 800,
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
  },

  errorBox: {
    padding: 14,
    borderRadius: 18,
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    color: "#9f1239",
    fontWeight: 900,
  },

  successBox: {
    padding: 14,
    borderRadius: 18,
    background: "#ecfdf5",
    border: "1px solid #a7f3d0",
    color: "#166534",
    fontWeight: 900,
  },

  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.42)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
    zIndex: 2000,
  },

  modalCard: {
    ...adminGlass.panel,
    width: "100%",
    maxWidth: 620,
    padding: 26,
  },

  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    marginBottom: 16,
  },

  modalTitle: {
    margin: 0,
    fontSize: 30,
    lineHeight: 1.05,
    letterSpacing: "-0.04em",
    fontWeight: 950,
    color: "#112042",
  },

modalCloseBtn: {
  width: 46,
  height: 46,
  borderRadius: 16,
  border: "1px solid rgba(255,255,255,0.7)",
  background: "#ffffff",
  color: "#0f172a",
  fontSize: 24,
  fontWeight: 900,
  cursor: "pointer",

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  lineHeight: 1,
  padding: 0,
  flexShrink: 0,
},

  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 18,
  },
};